import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';

const COLORS = {
  light: { core: '#00E5FF', edge: '#00B4D8', trail: '0, 229, 255',  glowSolid: '#00E5FF' },   // cyan
  dark:  { core: '#A855F7', edge: '#7C3AED', trail: '168, 85, 247', glowSolid: '#A855F7' },   // royal purple
};

/**
 * Custom glowing-arrow cursor + comet trail.
 *
 *  • Cyan in light mode → royal purple in dark mode (live theme-reactive)
 *  • Native OS cursor hidden via `cursor: none` (caret preserved on inputs)
 *  • Trail drawn on a full-screen canvas with mix-blend: screen
 *  • Disabled on touch / coarse-pointer devices
 */
export default function CursorTrail() {
  const cursorRef = useRef(null);
  const canvasRef = useRef(null);
  // Detect pointer capability synchronously so refs mount on first render.
  const [enabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });

  const themeCtx = useTheme();
  const dark = themeCtx?.dark ?? false;
  const palette = dark ? COLORS.dark : COLORS.light;
  // Use a ref so the rAF tick reads the latest palette without restarting the
  // whole effect when the theme toggles.
  const paletteRef = useRef(palette);
  useEffect(() => { paletteRef.current = palette; }, [palette]);

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    document.documentElement.classList.add('custom-cursor-active');

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let points = [];
    let raf = 0;
    let visible = false;

    const setVisible = (v) => {
      visible = v;
      const op = v ? '1' : '0';
      if (cursorRef.current) cursorRef.current.style.opacity = op;
      if (canvas) canvas.style.opacity = op;
    };

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      points.push({ x: mouseX, y: mouseY, time: performance.now() });
      if (points.length > 60) points.shift();
      if (!visible) setVisible(true);
    };
    const onLeave = () => setVisible(false);
    const onDown = () => { if (cursorRef.current) cursorRef.current.style.transform += ' scale(0.85)'; };

    const MAX_AGE = 550;

    const tick = () => {
      const c = paletteRef.current;
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
      }
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      const now = performance.now();
      points = points.filter(p => now - p.time < MAX_AGE);

      if (points.length > 1) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (let i = 1; i < points.length; i++) {
          const p0 = points[i - 1];
          const p1 = points[i];
          const age = now - p1.time;
          const alpha = Math.max(0, 1 - age / MAX_AGE);
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.lineWidth = 4 * alpha + 1;
          ctx.strokeStyle = `rgba(${c.trail}, ${alpha * 0.9})`;
          ctx.shadowBlur = 14 * alpha;
          ctx.shadowColor = c.glowSolid;
          ctx.stroke();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', resize);
      document.documentElement.classList.remove('custom-cursor-active');
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <canvas
        ref={canvasRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[9998] opacity-0 transition-opacity duration-200"
        style={{ mixBlendMode: 'screen' }}
      />
      <div
        ref={cursorRef}
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[9999] opacity-0 transition-opacity duration-200"
        style={{ willChange: 'transform' }}
      >
        <svg
          width="22"
          height="26"
          viewBox="0 0 22 26"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: `drop-shadow(0 0 4px ${palette.glowSolid}) drop-shadow(0 0 10px rgba(${palette.trail},0.55))` }}
        >
          <path
            d="M1 1 L1 19 L6 15 L9 23 L12.5 22 L9.5 14 L17 14 Z"
            fill={palette.core}
            stroke="#ffffff"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </>
  );
}
